/**
 * E06-F05-T16 — manual check of a company the AI check could not read.
 *
 * Call with Ksenia 2026-08-10 (meetings/08.10.26): large companies block the scraper,
 * the row ends as "Failed — open it manually to check", but there was no place to
 * record what the person then saw on the website. This contract is that place: the
 * person opens the site, sets a verdict (pass / partial / fail) on each of the six
 * scored criteria, optionally with a note and the page they read it on, and the
 * server scores the row with the SAME deterministic weighted math the AI check uses
 * (no AI call — manual input is free).
 *
 * Provenance: the row carries a private `evidence.__manualCheck` mark (who, when,
 * which page) — the "these data were entered by hand" flag every screen reads through
 * {@link readManualCheck}. Each criterion's evidence quote is the person's note,
 * prefixed with {@link MANUAL_EVIDENCE_PREFIX}, so the Evidence drawer also shows it.
 *
 * Imports only zod (self-contained for the barrel).
 */
import { z } from 'zod';
/** The six scored criteria (same set as REVIEW_CRITERIA_KEYS / SCORE_CRITERIA). */
export const MANUAL_CHECK_CRITERIA = [
    'industry',
    'subIndustry',
    'geo',
    'size',
    'founded',
    'services',
];
/** A person's verdict — never 'unclear': a human either saw it or did not. */
export const manualCheckVerdictSchema = z.enum(['pass', 'partial', 'fail']);
/** Private evidence key of the manual-check mark (`__` = never serialised as evidence). */
export const MANUAL_CHECK_KEY = '__manualCheck';
/** Prefix of every evidence quote a manual check writes. */
export const MANUAL_EVIDENCE_PREFIX = 'Manual check: ';
/** The evidence text of a criterion the person confirmed without a note. */
export const MANUAL_NO_NOTE = 'confirmed on the company website by a person.';
/** The label every screen shows next to manually entered check data. */
export const MANUAL_CHECK_LABEL = 'Checked manually';
/**
 * Normalise the "page you checked" input: '' stays ''; a bare host gets `https://`;
 * anything that is not a parseable http(s) URL (e.g. `javascript:`) → null (rejected).
 * Pure.
 */
export function normalizeManualSourceUrl(raw) {
    const v = raw.trim();
    if (!v)
        return '';
    const withScheme = /^[a-z][a-z0-9+.-]*:/i.test(v) ? v : `https://${v}`;
    // scheme http(s) · host with a dot (no credentials) · optional port · optional path;
    // no whitespace / quotes / angle brackets anywhere (the value becomes an href).
    const m = /^(https?):\/\/([a-z0-9-]+(?:\.[a-z0-9-]+)+)(:\d{1,5})?([/?#][^\s"'<>]*)?$/i.exec(withScheme);
    if (!m)
        return null;
    return `${m[1].toLowerCase()}://${m[2].toLowerCase()}${m[3] ?? ''}${m[4] ?? ''}`;
}
/**
 * POST /research/:requestId/companies/:rowId/manual-check — body. All six verdicts are
 * required: the weighted score counts a missing criterion as a fail, so the form asks
 * for each one instead of silently scoring a gap.
 */
export const manualCheckBodySchema = z.object({
    verdicts: z.object({
        industry: manualCheckVerdictSchema,
        subIndustry: manualCheckVerdictSchema,
        geo: manualCheckVerdictSchema,
        size: manualCheckVerdictSchema,
        founded: manualCheckVerdictSchema,
        services: manualCheckVerdictSchema,
    }),
    /** What the person saw, per criterion (optional). */
    notes: z
        .object({
        industry: z.string().trim().max(1000).optional(),
        subIndustry: z.string().trim().max(1000).optional(),
        geo: z.string().trim().max(1000).optional(),
        size: z.string().trim().max(1000).optional(),
        founded: z.string().trim().max(1000).optional(),
        services: z.string().trim().max(1000).optional(),
    })
        .optional()
        .default({}),
    /**
     * The page the person checked (defaults to the company website on the server). It
     * becomes an evidence link (`href`), so only http(s) is accepted; a bare host
     * (`bigcorp.com/about`) is normalised to `https://…`.
     */
    sourceUrl: z
        .string()
        .trim()
        .max(500)
        .optional()
        .default('')
        .transform((v) => normalizeManualSourceUrl(v))
        .refine((v) => v !== null, { message: 'The page must be an http(s) address' })
        .transform((v) => v ?? ''),
    /** A free comment that goes into the rationale. */
    comment: z.string().trim().max(1000).optional().default(''),
});
/** The manual-check mark as the wire carries it (PipelineRow / ReviewRow `.manualCheck`). */
export const manualCheckMarkSchema = z.object({
    /** users.id of the person who entered the data. */
    by: z.string(),
    /** ISO timestamp of the entry. */
    at: z.string(),
    /** The page the person checked (null when none was given and no website is known). */
    url: z.string().nullable().default(null),
});
/**
 * Read the manual-check mark from a row's evidence blob; null when the row was not
 * checked by hand (or the mark is malformed). Pure.
 */
export function readManualCheck(evidence) {
    if (!evidence || typeof evidence !== 'object')
        return null;
    const raw = evidence[MANUAL_CHECK_KEY];
    const parsed = manualCheckMarkSchema.safeParse(raw);
    return parsed.success ? parsed.data : null;
}
//# sourceMappingURL=research-manual-check.js.map